'use client';

import type { ButtonProps } from '@magicschool/spellbook/components/Button';
import { FilterChipCalendarSlot, FilterChipDateTriggerSlot } from '@magicschool/spellbook/components/FilterChipDate/slots';
import { Popover, type PopoverProps } from '@magicschool/spellbook/components/Popover';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

export type FilterChipDateProps = Omit<ButtonProps, 'value'> & {
  'data-testid'?: string;
  /** The selected date. */
  value?: Date;
  /** The default selected date. */
  defaultValue?: Date;
  /** Callback fired when the date changes. */
  onDateChange?: (date: Date | undefined) => void;
  /** The language locale to use for formatting dates. Set to 'en-US' by default. See [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat#locales_and_options) for more information on locales. */
  locale?: string;
} & Pick<PopoverProps, 'defaultOpen' | 'open' | 'onOpenChange'>;

const FilterChipDateBase = forwardRef<HTMLButtonElement, FilterChipDateProps>(
  (
    {
      className,
      children,
      'data-testid': dataTestId,
      value,
      defaultValue,
      onDateChange,
      defaultOpen,
      open: providedOpen,
      onOpenChange,
      locale,
      ...props
    },
    forwardedRef,
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(defaultOpen || providedOpen || false);
    const [internalDate, setInternalDate] = useState<Date | undefined>(defaultValue);

    // When value transitions from a defined Date to undefined, reset internal state.
    // This handles cases like "Clear All" in DataGrid where the parent clears the value
    // by passing undefined, which would otherwise leave FilterChipDate in stale uncontrolled mode.
    const prevValueRef = useRef(value);
    if (prevValueRef.current !== undefined && value === undefined) {
      setInternalDate(undefined);
    }
    prevValueRef.current = value;

    const isControlled = value !== undefined;
    const selectedDate = isControlled ? value : internalDate;

    const handleDateChange = (date: Date | undefined) => {
      setInternalDate(date);
      setOpen(false);
      onDateChange?.(date);
    };

    const handleOpenChange = (open: boolean) => {
      setOpen(open);
      onOpenChange?.(open);
    };

    useImperativeHandle(forwardedRef, () => buttonRef.current!, []);

    const { slots } = useSlots(children, {
      /**
       * @TODO [SPE-165] Add in Tooltip support for FilterChipDate
       * https://linear.app/magicschool/issue/SPE-165/add-in-tooltip-support-for-filterchipdate
       */
      trigger: {
        Component: FilterChipDateTriggerSlot,
        props: { selectedDate, locale, 'data-testid': dataTestId, ref: buttonRef, ...props },
      },
      calendar: {
        Component: FilterChipCalendarSlot,
        props: {
          selected: selectedDate,
          onSelect: handleDateChange,
          disabled: props.disabled,
          locale,
        },
      },
    });

    return (
      <Popover open={open} onOpenChange={handleOpenChange} modal>
        <Popover.Trigger asChild>{slots.trigger}</Popover.Trigger>
        <Popover.Content>{slots.calendar}</Popover.Content>
      </Popover>
    );
  },
);

/**
 * A type of filter chip that allows users to select a date from a `Calendar` popover component.
 * As of now, it only supports a single date selection, and only allows for a custom trigger button, which is not recommended to be overridden.
 *
 * ### When to use
 * - Filtering a list or table by a single date from a compact chip in a filter bar
 *
 * ### When NOT to use
 * - Date entry inside a form: use `DateInput`
 * - Choosing from a fixed set of options: use `FilterChipSelect`
 *
 * ### Available Slots
 * - `<FilterChipDate.Trigger>`: The main trigger for the FilterChipDate, which opens the calendar popover. This is an opinionated Button component that is not overridable.
 * - `<FilterChipDate.Calendar>`: (default render) The calendar popover content. This is where the user can select a date. Only pass this slot to pass custom props to the Calendar component.
 *
 * ### Functional Props
 * - `value`: The currently selected date (Date object).
 * - `defaultValue`: The default selected date (Date object).
 * - `onDateChange`: Callback function that is called when the date changes, receiving the new date as an argument.
 * - `locale`: The locale to use for formatting the date.
 * - `defaultOpen`: Whether the popover should be open by default.
 * - `open`: Controlled prop to manage the open state of the popover.
 * - `onOpenChange`: Callback function that is called when the open state of the popover changes.
 *
 * @headless Composes Popover with no wrapper element; target `filter-chip-date-trigger` and `filter-chip-date-calendar`.
 */
export const FilterChipDate = Object.assign(FilterChipDateBase, {
  Trigger: FilterChipDateTriggerSlot,
  Calendar: FilterChipCalendarSlot,
});
